export interface DetectedCardFields {
  cardNumber: HTMLInputElement | null;
  cardExp: HTMLInputElement | null;
  cardExpMonth: HTMLInputElement | null;
  cardExpYear: HTMLInputElement | null;
  cardCvv: HTMLInputElement | null;
}

export interface CardFieldSelectors {
  cardNumber?: string;
  cardExp?: string;
  cardExpMonth?: string;
  cardExpYear?: string;
  cardCvv?: string;
}

const CARD_NUMBER_IDS = [
  'card_number',
  'cardNumber',
  'card-number',
  'cc_number',
  'cc-number',
  'credit_card_number',
  'credit-card-number',
];

const CARD_NUMBER_NAMES = [
  'card_number',
  'cardNumber',
  'card-number',
  'cc_number',
  'cc-number',
  'credit_card_number',
];

const CVV_IDS = ['cvv', 'cvc', 'security_code', 'security-code'];
const CVV_NAMES = ['cvv', 'cvc', 'security_code', 'securityCode'];

const EXP_IDS = [
  'card_exp',
  'card_expiration',
  'card-expiration',
  'expiration',
  'expiry',
];
const EXP_NAMES = [
  'card_exp',
  'cardExpiration',
  'card_expiration',
  'expiration',
  'expiry',
];

const EXP_MONTH_IDS = ['card_exp_month', 'exp_month', 'expiration_month'];
const EXP_MONTH_NAMES = [
  'card_exp_month',
  'exp_month',
  'expiration_month',
  'cc_exp_month',
];

const EXP_YEAR_IDS = ['card_exp_year', 'exp_year', 'expiration_year'];
const EXP_YEAR_NAMES = [
  'card_exp_year',
  'exp_year',
  'expiration_year',
  'cc_exp_year',
];

const CARD_NUMBER_LABELS = [
  'card number',
  'credit card number',
  'número do cartão',
  'numero do cartao',
];

const CVV_LABELS = ['security code', 'cvv', 'cvc'];
const EXP_LABELS = ['expiration', 'expiry', 'validade', 'data de validade'];

function isVisibleInput(el: HTMLInputElement): boolean {
  if (el.type === 'hidden' || el.disabled) return false;
  return true;
}

export function querySelectorInput(selector: string): HTMLInputElement | null {
  const el = document.querySelector(selector);
  if (el instanceof HTMLInputElement && isVisibleInput(el)) return el;
  return null;
}

function findInputByIdOrName(
  ids: string[],
  names: string[]
): HTMLInputElement | null {
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el instanceof HTMLInputElement && isVisibleInput(el)) return el;
  }
  for (const name of names) {
    const elements = document.querySelectorAll(`input[name="${name}"]`);
    for (const el of elements) {
      if (el instanceof HTMLInputElement && isVisibleInput(el)) return el;
    }
  }
  return null;
}

function findInputByAutocomplete(value: string): HTMLInputElement | null {
  const elements = document.querySelectorAll(`input[autocomplete="${value}"]`);
  for (const el of elements) {
    if (el instanceof HTMLInputElement && isVisibleInput(el)) return el;
  }
  return null;
}

function findInputByLabelTexts(texts: string[]): HTMLInputElement | null {
  const labels = document.querySelectorAll('label');
  for (const label of labels) {
    const text = label.textContent?.trim().toLowerCase() || '';
    for (const pattern of texts) {
      if (!text.includes(pattern.toLowerCase())) continue;

      const forId = label.getAttribute('for');
      if (forId) {
        const el = document.getElementById(forId);
        if (el instanceof HTMLInputElement && isVisibleInput(el)) return el;
      }

      const input = label.querySelector('input');
      if (input instanceof HTMLInputElement && isVisibleInput(input)) {
        return input;
      }
    }
  }
  return null;
}

function firstFound(
  ...finders: Array<() => HTMLInputElement | null>
): HTMLInputElement | null {
  for (const finder of finders) {
    const found = finder();
    if (found) return found;
  }
  return null;
}

export function detectCardFields(
  customSelectors?: CardFieldSelectors
): DetectedCardFields {
  const result: DetectedCardFields = {
    cardNumber: null,
    cardExp: null,
    cardExpMonth: null,
    cardExpYear: null,
    cardCvv: null,
  };

  if (customSelectors?.cardNumber) {
    result.cardNumber = querySelectorInput(customSelectors.cardNumber);
  }
  if (customSelectors?.cardExp) {
    result.cardExp = querySelectorInput(customSelectors.cardExp);
  }
  if (customSelectors?.cardExpMonth) {
    result.cardExpMonth = querySelectorInput(customSelectors.cardExpMonth);
  }
  if (customSelectors?.cardExpYear) {
    result.cardExpYear = querySelectorInput(customSelectors.cardExpYear);
  }
  if (customSelectors?.cardCvv) {
    result.cardCvv = querySelectorInput(customSelectors.cardCvv);
  }

  if (!result.cardNumber) {
    result.cardNumber = firstFound(
      () => findInputByAutocomplete('cc-number'),
      () => findInputByIdOrName(CARD_NUMBER_IDS, CARD_NUMBER_NAMES),
      () => findInputByLabelTexts(CARD_NUMBER_LABELS)
    );
  }

  if (!result.cardCvv) {
    result.cardCvv = firstFound(
      () => findInputByAutocomplete('cc-csc'),
      () => findInputByIdOrName(CVV_IDS, CVV_NAMES),
      () => findInputByLabelTexts(CVV_LABELS)
    );
  }

  if (!result.cardExpMonth) {
    result.cardExpMonth = firstFound(
      () => findInputByAutocomplete('cc-exp-month'),
      () => findInputByIdOrName(EXP_MONTH_IDS, EXP_MONTH_NAMES)
    );
  }

  if (!result.cardExpYear) {
    result.cardExpYear = firstFound(
      () => findInputByAutocomplete('cc-exp-year'),
      () => findInputByIdOrName(EXP_YEAR_IDS, EXP_YEAR_NAMES)
    );
  }

  if (!result.cardExp) {
    result.cardExp = firstFound(
      () => findInputByAutocomplete('cc-exp'),
      () => findInputByIdOrName(EXP_IDS, EXP_NAMES),
      () => findInputByLabelTexts(EXP_LABELS)
    );
  }

  return result;
}

export function isCardNumberFilled(input: HTMLInputElement | null): boolean {
  if (!input) return false;
  const digits = input.value.replace(/\D/g, '');
  return digits.length >= 12;
}

export function isCvvFilled(input: HTMLInputElement | null): boolean {
  if (!input) return false;
  const digits = input.value.replace(/\D/g, '');
  return digits.length === 3 || digits.length === 4;
}

export function isCardExpFilled(
  cardExp: HTMLInputElement | null,
  cardExpMonth: HTMLInputElement | null,
  cardExpYear: HTMLInputElement | null
): boolean {
  if (cardExp) {
    const value = cardExp.value.trim();
    if (/^\d{1,2}\s*\/\s*\d{2,4}$/.test(value)) return true;
    const digits = value.replace(/\D/g, '');
    return digits.length >= 4;
  }

  if (cardExpMonth && cardExpYear) {
    const month = cardExpMonth.value.replace(/\D/g, '');
    const year = cardExpYear.value.replace(/\D/g, '');
    if (month.length >= 1 && year.length >= 2) {
      const monthNum = parseInt(month, 10);
      return monthNum >= 1 && monthNum <= 12;
    }
  }

  return false;
}

export function areAllCardFieldsFilled(fields: DetectedCardFields): boolean {
  return (
    isCardNumberFilled(fields.cardNumber) &&
    isCvvFilled(fields.cardCvv) &&
    isCardExpFilled(fields.cardExp, fields.cardExpMonth, fields.cardExpYear)
  );
}

export function collectFieldInputs(
  fields: DetectedCardFields
): HTMLInputElement[] {
  const inputs: HTMLInputElement[] = [];
  const seen = new Set<HTMLInputElement>();

  for (const field of [
    fields.cardNumber,
    fields.cardExp,
    fields.cardExpMonth,
    fields.cardExpYear,
    fields.cardCvv,
  ]) {
    if (field && !seen.has(field)) {
      seen.add(field);
      inputs.push(field);
    }
  }

  return inputs;
}
